import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { canReview } from '../../utils/stateHelpers';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import { useLearner } from '../../hooks/useLearner';
import { getRandomBuddyAsset, SLOT } from '../../lib/buddyManifest';
// CloudBand replaced by CSS background-image repeat-x div — no component needed
import s from './CelebrationPage.module.css';

const isCjk = (ch) => ch && /[\u4e00-\u9fff\u3400-\u4dbf]/.test(ch);

function StrokeOrderBox({ char, opacity = 1 }) {
  return (
    <div className={s.strokeBox} style={{ opacity }}>
      <span className={s.strokeBoxChar}>{char}</span>
    </div>
  );
}

export default function CelebrationPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const { storyId } = useParams();
  const location = useLocation();
  const story = location.state?.story || null;
  const { data: activeLearner } = useLearner();

  // The learner's chosen buddy in a CELEBRATE pose — picked once on mount and
  // held for the full screen duration (same lifetime as the old random pool pick).
  const [celebrationImg] = useState(
    () => getRandomBuddyAsset(activeLearner?.buddy?.slug, SLOT.CELEBRATE)
  );

  const practicingChars = useMemo(() => {
    if (!story?.characters) return [];
    const seen = new Set();
    return story.characters.filter((c) => {
      if (c.display_state !== 'practicing' || !isCjk(c.char)) return false;
      if (seen.has(c.char)) return false;
      seen.add(c.char);
      return true;
    });
  }, [story]);

  const newChars = useMemo(() => {
    if (!story?.characters) return [];
    const seen = new Set();
    return story.characters.filter((c) => {
      if (c.display_state !== 'new_introduction' || !isCjk(c.char)) return false;
      if (seen.has(c.char)) return false;
      seen.add(c.char);
      return true;
    });
  }, [story]);

  // FP-CLIENT-SUBMIT-GUARD-01: single-fire the Review entry CTA so a slow
  // transition can't be double-tapped into the review flow. Ref is the
  // synchronous guard; state drives the pending affordance. (The review submit
  // itself stays guarded by ReviewSummary.submittedRef — untouched here.)
  const reviewingRef = useRef(false);
  const [reviewing, setReviewing] = useState(false);

  function handleReviewNow() {
    if (reviewingRef.current) return;
    reviewingRef.current = true;
    setReviewing(true);
    navigate(`/story/review/${storyId}`, { state: { story } });
  }

  function handleBackHome() {
    navigate('/', { replace: true });
  }

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1}>
      {/* Cloud band — tiling background div, never clips at edges */}
      <div className={s.cloudBand} aria-hidden="true" />
      <div className={s.creamBg} />

      {/* Mascot — pinned to cloud/cream boundary, above content flow */}
      <div className={s.mascotWrap} aria-hidden="true">
        <img
          src={celebrationImg}
          alt=""
          className={s.mascotImg}
        />
      </div>

      {/* Content */}
      <div className={s.content}>
        {/* Hero: headline only — mascot is now absolutely positioned above */}
        <div className={s.heroSection}>
          <div className={s.headlineWrap}>
            <h1 className={s.headline}>
              {t('celebration.title')}
            </h1>
            <p className={s.headlineSubtitle}>
              {t('celebration.subtitle')}
            </p>
          </div>
        </div>

        {/* What you've learned today */}
        {(practicingChars.length > 0 || newChars.length > 0) ? (
          <div className={s.learnedSection}>
            <p className={s.learnedTitle}>
              {t('celebration.pickedUp')}
            </p>

            <div className={s.learnedColumns}>
              {/* From Practice — only render if there are practicing characters */}
              {practicingChars.length > 0 && (
                <div className={s.learnedColumn}>
                  <span className={s.learnedLabel}>{t('celebration.fromPractice')}</span>
                  <div className={s.charTiles}>
                    {practicingChars.map((c, i) => (
                      <StrokeOrderBox key={i} char={c.char} opacity={0.8} />
                    ))}
                  </div>
                </div>
              )}

              {/* New Today — only render if there are new characters */}
              {newChars.length > 0 && (
                <div className={s.learnedColumn}>
                  <span className={s.learnedLabel}>{t('celebration.newToday')}</span>
                  <div className={s.charTiles}>
                    {newChars.map((c, i) => (
                      <StrokeOrderBox key={i} char={c.char} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={s.learnedSection}>
            <p className={s.learnedTitle}>{t('celebration.pickedUp')}</p>
            <p className={s.learnedLabel}>{t('celebration.noCharsYet')}</p>
          </div>
        )}

        {/* Buttons */}
        <div className={s.buttonRow}>
          {canReview(story) && (
            <button
              type="button"
              className={`${s.btnSecondary}${reviewing ? ` ${s.btnPending}` : ''}`}
              onClick={handleReviewNow}
              disabled={reviewing}
              aria-busy={reviewing}
            >
              {reviewing && <span className={s.btnSpinner} aria-hidden="true" />}
              {t('celebration.reviewNow')}
            </button>
          )}
          <button
            type="button"
            className={s.tertiaryLink}
            onClick={handleBackHome}
          >
            {t('celebration.backHome')}
          </button>
        </div>
      </div>
    </main>
  );
}
